import { Hls, HlsVideoElement } from 'hls-video-element';
import { CastableMediaMixin } from 'castable-video/castable-mixin.js';
import { VASTAdsMixin } from '../vast-hls-video.js';
import { getBestCodecType, isCodecSupported } from '../utils/codec-filter.js';
import { StreamDrmEngine } from '../utils/stream-drm-engine.js';
import {
  isChrome,
  isFairPlayBrowser,
  isIOS,
  isSafari,
} from '../utils/browser-detection.js';

class BunnyStreamVideoElement
  extends VASTAdsMixin(CastableMediaMixin(HlsVideoElement)) {
  static get observedAttributes() {
    return (super.observedAttributes || []).concat([
      'drm-robustness',
      'drm-version',
      'video-id',
      'library-id',
      'drm-engine',
      'drm-provider',
      'is-jit',
      'base-url',
      'level-cap',
      'start-time',
      'chromecast',
      'airplay',
    ]);
  }

  _waitingKeys = false;
  _edrmEnabled = false;
  _videoId = this.getAttribute('video-id');
  _libraryId = this.getAttribute('library-id');
  _drmEngine = this.getAttribute('drm-engine');
  _drmProvider = this.getAttribute('drm-provider');
  _drmVersion = this.#parseDrmVersion(this.getAttribute('drm-version'));
  _baseUrl = this.getAttribute('base-url') || 'https://video.bunnycdn.com';
  _levelCap = this.getAttribute('level-cap') === 'true';
  _startTime = parseFloat(this.getAttribute('start-time') || '0');
  _fragmentsLoaded = 0;
  _filteredLevels = null;

  /**
   * Parse DRM version attribute value to integer
   * @param {string|null} value - Attribute value to parse
   * @returns {number|null} Parsed integer or null if value is invalid/absent
   */
  #parseDrmVersion(value) {
    return value ? parseInt(value, 10) : null;
  }

  /**
   * Check if preload is enabled based on preload attribute
   * HTML5 preload values: "auto" (load entire video), "metadata" (load metadata only), "none" (no preload)
   * @returns {boolean} True if preload is "auto", false otherwise (none or metadata)
   */
  #shouldPreload() {
    const preloadAttr = this.getAttribute('preload');
    return preloadAttr === 'auto';
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'drm-version') {
      this._drmVersion = this.#parseDrmVersion(newVal);
      this._edrmEnabled = this._drmVersion === 1;
    }
    if (name === 'video-id') this._videoId = newVal;
    if (name === 'library-id') this._libraryId = newVal;
    if (name === 'drm-engine') this._drmEngine = newVal;
    if (name === 'base-url') {
      this._baseUrl = newVal || 'https://video.bunnycdn.com';
    }
    if (name === 'drm-provider') this._drmProvider = newVal;
    if (name === 'is-jit') {
      this.isJit = newVal;
    }
    if (name === 'level-cap') {
      this._levelCap = this.getAttribute('level-cap') === 'true';
    }
    if (name === 'start-time') {
      this._startTime = parseFloat(newVal || '0');
    }
    if (name === 'chromecast') {
      this.#updateDisableRemotePlayback();
    }
    if (name === 'airplay') {
      this.#updateDisableRemotePlayback();
    }
    super.attributeChangedCallback?.(name, oldVal, newVal);
  }

  constructor() {
    super();
    this.mediaSourceSupported = this.#checkMediaSourceSupport();
    this.isJit = false;

    this._edrmEnabled = this._drmVersion === 1;

    this.#updateDisableRemotePlayback();

    // Make element focusable for keyboard events
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    // Add keyboard event listeners for volume control
    this.addEventListener('keydown', this.#handleKeyDown.bind(this));
  }

  init() {
    super.init();
    // Ensure shadow root exists (HlsVideoElement likely creates it, but confirm)
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
    }

    // When preload is disabled, HLS.js won't start loading automatically (autoStartLoad: false)
    // We need to start loading when the user initiates playback
    if (!this.#shouldPreload()) {
      // Start loading when play is triggered (added early to prevent race conditions)
      this.addEventListener('play', () => {
        if (this.api) {
          this.api.startLoad();
        }
      }, { once: true });
    }
  }

  /**
   * Detect the maximum available Widevine robustness level supported by the browser
   * @returns {Promise<string>} The highest supported robustness level
   */
  async #detectMaxRobustness() {
    // Robustness levels to test in priority order (L1 hardware -> L3 software)
    const robustnessLevels = [
      'HW_SECURE_CRYPTO', // L1: Hardware-backed crypto
      'HW_SECURE_DECODE', // L1: Hardware-backed decode
      'SW_SECURE_CRYPTO', // L3: Software-based
    ];

    // Test each robustness level using EME API
    for (const level of robustnessLevels) {
      try {
        const config = [{
          initDataTypes: ['cenc'],
          videoCapabilities: [{
            contentType: 'video/mp4; codecs="avc1.42E01E"',
            robustness: level,
          }],
        }];

        await navigator.requestMediaKeySystemAccess(
          'com.widevine.alpha',
          config,
        );
        console.debug(`Detected max Widevine robustness: ${level}`);
        return level;
      } catch (_error) {
        // This level not supported, try next
        continue;
      }
    }

    // Fallback to software-based if detection fails
    console.debug(
      'Widevine robustness detection failed, using SW_SECURE_CRYPTO',
    );
    return 'SW_SECURE_CRYPTO';
  }

  async #getRobustness() {
    const drmRobustness = this.getAttribute('drm-robustness');

    // If backend provides a minimum robustness level, respect it
    if (drmRobustness) {
      switch (+drmRobustness) {
        case 1:
          return {
            videoRobustness: 'HW_SECURE_DECODE',
          };
        case 2:
          return {
            videoRobustness: 'HW_SECURE_CRYPTO',
          };
        case 3:
          return {
            videoRobustness: 'SW_SECURE_CRYPTO',
          };
      }
    }

    // No backend setting: detect and use maximum available robustness
    const maxRobustness = await this.#detectMaxRobustness();
    return {
      videoRobustness: maxRobustness,
    };
  }

  #checkMediaSourceSupport() {
    try {
      return typeof MediaSource !== 'undefined';
    } catch (_e) {
      return false;
    }
  }

  /**
   * Update disableRemotePlayback based on chromecast and airplay attributes and browser type
   */
  #updateDisableRemotePlayback() {
    const chromecastDisabled = this.getAttribute('chromecast') !== 'true' &&
      isChrome();
    const airplayDisabled = this.getAttribute('airplay') === 'false' &&
      (isSafari() || isIOS());

    this.disableRemotePlayback = chromecastDisabled || airplayDisabled;
  }

  /**
   * Get the underlying HTMLVideoElement
   * @returns {HTMLVideoElement|null} The video element or null if not found
   */
  #getVideoElement() {
    // Check HLS.js api media reference first (most reliable)
    if (this.api && this.api.media) {
      return this.api.media;
    }

    // Fallback to shadow DOM
    if (this.shadowRoot) {
      const videoInShadow = this.shadowRoot.querySelector('video');
      if (videoInShadow) return videoInShadow;
    }

    // Last resort: direct child
    return this.querySelector('video');
  }

  /**
   * Setup start time seeking when video metadata loads using media-chrome seek request
   */
  #setupStartTimeSeeking() {
    const seekToStartTime = () => {
      this.dispatchEvent(
        new CustomEvent('mediaseekrequest', {
          composed: true,
          bubbles: true,
          detail: this._startTime,
        }),
      );
    };

    // If video is already ready, seek immediately
    if (this.readyState >= 1) {
      seekToStartTime();
    } else {
      // Wait for metadata to load
      this.addEventListener('loadedmetadata', seekToStartTime, { once: true });
    }
  }

  /**
   * Handle keyboard events for volume control
   */
  #handleKeyDown(event) {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.volume = Math.min(1, this.volume + 0.1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.volume = Math.max(0, this.volume - 0.1);
        break;
    }
  }

  /**
   * Apply codec filtering to HLS levels (for UI purposes only)
   * Unlike the legacy player, we don't remove levels from hls.js to preserve ABR functionality
   */
  #applyCodecFilteringForUI(levels) {
    if (!Array.isArray(levels) || !this.mediaSourceSupported) return;

    // Determine the best codec type to keep
    const bestCodecType = getBestCodecType(levels);

    if (!bestCodecType) {
      return; // Keep all levels
    }

    // Filter levels to preferred codec while preserving original indices for HLS.js switching
    const levelsToKeep = levels
      .map((level, index) => ({ ...level, originalIndex: index }))
      .filter((level) => {
        const codecSet = level.codecSet || '';
        return codecSet.includes(bestCodecType) && isCodecSupported(level);
      });

    this._filteredLevels = levelsToKeep;

    // Communicate filtered levels to the rendition menu
    const renditionMenu = this.getRootNode()?.querySelector(
      'bunny-rendition-menu',
    );
    if (renditionMenu && 'filteredLevels' in renditionMenu) {
      renditionMenu.filteredLevels = levelsToKeep;
    }
  }

  /**
   * Parse URL path into key-value pairs
   * @param {string} url - URL to parse
   * @returns {Map<string, string>} Map of path segments
   */
  #parseUrlPath(url) {
    const routes = new Map();
    const p = new URL(url).pathname.split('/');
    for (let i = 1; i < p.length - 1; i += 2) {
      routes.set(p[i], p[i + 1]);
    }
    return routes;
  }

  generateDrmRequest(initDataType, initData, _keyContext) {
    return { initDataType, initData };
  }

  getRoutes(request) {
    return this.#parseUrlPath(request);
  }

  /**
   * Get token query parameters from URL
   * @returns {string} Query string with token and expires params, or empty string
   */
  #getTokenQueryParams() {
    const url = new URL(globalThis.location.href);
    const token = url.searchParams.get('token') || '';
    const expires = parseInt(url.searchParams.get('expires') || '0', 10);

    if (token && expires) {
      return `token=${token}&expires=${expires}`;
    }
    return '';
  }

  /**
   * Get token query parameters with leading ? for URL construction
   * @returns {string} Query string with ? prefix, or empty string
   */
  #getTokenQueryString() {
    const params = this.#getTokenQueryParams();
    return params ? `?${params}` : '';
  }

  /**
   * Build FairPlay license URL
   * @returns {string} FairPlay license URL
   */
  #getFairPlayLicenseUrl() {
    const queryParams = this.#getTokenQueryParams();
    const separator = queryParams ? '&' : '';
    return `${this._baseUrl}/FairPlay/${this._libraryId}/license?videoId=${this._videoId}${separator}${queryParams}`;
  }

  /**
   * Build FairPlay certificate URL
   * @returns {string} FairPlay certificate URL
   */
  #getFairPlayCertificateUrl() {
    return `${this._baseUrl}/FairPlay/${this._libraryId}/certificate`;
  }

  /**
   * Build Widevine license URL
   * @returns {string} Widevine license URL
   */
  #getWidevineLicenseUrl() {
    const queryString = this.#getTokenQueryString();
    return `${this._baseUrl}/WidevineLicense/${this._libraryId}/${this._videoId}${queryString}`;
  }

  async load() {
    // Configure basic HLS.js defaults (non-DRM settings)
    const shouldPreload = this.#shouldPreload();
    const basicHlsConfig = {
      debug: false,
      //testBandwidth: false,
      emeEnabled: this._edrmEnabled && this._drmEngine != 'internal',
      enableWorker: true,
      autoStartLoad: shouldPreload,
      maxMaxBufferLength: 120,
      maxBufferLength: 120,
      minBufferLength: 8,
      // ABR (Adaptive Bitrate) configuration for better initial quality selection
      abrEwmaDefaultEstimate: 5000000, // 5 Mbps initial bandwidth estimate
      startLevel: -1, // Auto-select initial level based on bandwidth estimate
    };

    // Level cap configuration (matching legacy behavior)
    if (this._levelCap) {
      basicHlsConfig.capLevelToPlayerSize = true;
      basicHlsConfig.ignoreDevicePixelRatio = true;
    }

    Object.assign(Hls.DefaultConfig, basicHlsConfig);

    // Only configure DRM if drmVersion is 1
    if (this._edrmEnabled) {
      if (this._drmEngine === 'internal') {
        // Create StreamDrmEngine but don't initialize yet
        globalThis.streamDrmEngine = new StreamDrmEngine(
          this.#getFairPlayLicenseUrl(),
          this.#getFairPlayCertificateUrl(),
          0,
        );
      } else {
        // Configure HLS.js DRM for non-internal engines
        const isFairPlay = isFairPlayBrowser();
        const hlsDrmConfig = {
          emeEnabled: true,
          drmSystemOptions: isFairPlay ? {} : await this.#getRobustness(),
          drmSystems: {
            'com.widevine.alpha': {
              licenseUrl: this.#getWidevineLicenseUrl(),
              generateRequest: this.generateDrmRequest,
            },
            'com.apple.fps': {
              licenseUrl: this.#getFairPlayLicenseUrl(),
              serverCertificateUrl: this.#getFairPlayCertificateUrl(),
            },
          },
        };

        Object.assign(Hls.DefaultConfig, hlsDrmConfig);
      }
    }

    // Call parent load
    await super.load();

    // Handle start time seeking
    if (this._startTime > 0) {
      this.#setupStartTimeSeeking();
    }

    // Only proceed with DRM initialization if DRM is enabled
    if (this._edrmEnabled && this._drmEngine === 'internal') {
      try {
        const realVideoElement = this.#getVideoElement();
        if (!realVideoElement) {
          throw new Error(
            'Could not find real HTMLVideoElement - encrypted events will fail',
          );
        }

        // Initialize StreamDrmEngine with the REAL video element
        await globalThis.streamDrmEngine.initFairPlay(realVideoElement);
      } catch (error) {
        console.error('❌ StreamDrmEngine initialization failed:', error);
        throw error;
      }

      return;
    }

    // Only proceed if we have HLS.js support and an api instance
    if (!this.api || !this.src) {
      return;
    }

    // Configure xhrSetup if handler is available
    if (this.isJit === 'true' || this.isJit === true || this.isJit === '1') {
      this.api.config.xhrSetup = (xhr, url) => {
        // Extract the session from the querystring
        const requestUrl = new URL(url);

        const routes = this.getRoutes(url);

        const guid = routes.get('guid');
        const session = routes.get('session');

        xhr.open(
          'GET',
          `/${guid}/${session}/${requestUrl.pathname.split('/').pop()}`,
        );
      };
    }

    // Bind HLS.js events
    this.api.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
      this.#applyCodecFilteringForUI(data.levels);
    });

    this.api.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          console.debug('fatal network error encountered, trying to recover');
          this.api.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          console.debug('fatal media error encountered, trying to recover');
          this.api.recoverMediaError();
          break;
        default:
          console.debug('unrecoverable error');
          this.api.destroy();
          break;
      }
    });
  }
}

customElements.define('bunny-stream-video', BunnyStreamVideoElement);

export default BunnyStreamVideoElement;
